/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useState, useEffect, useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box } from '@mui/system';
import { Dialog, IconButton, Typography } from '@mui/material';
import { TextField as SpectrumTextField } from '@bestdoctor/spectrum-uikit/TextField';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';
import { format, parse, isValid } from 'date-fns';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { dialogStyleAddModal } from 'ui/modals/modalAdd/modalAdd';

import { useSearchPrograms } from 'hooks/programs/useSearchProgram';
import { mapProgramsToSelectOptions } from 'utils/mapProgramsToSelectOptions';

import { TooltipActionsInfo } from 'ui/tooltips/tooltipAction';

import { EditPatientPassModalProps } from 'interfaces/patients/patientInterfaces';

import {
  modalHeaderStyle,
  modalTitleStyle,
  modalSubtitleStyle,
  modalFormStyle,
  modalFormScrollStyle,
  modalFormWrapStyle,
  modalFormWrapInputsStyle,
  modalFormInputStyle,
} from 'ui/modals/modalGlobal';

import { themeColor, grayLightDarkOpacity } from 'utils/uiConstants';
import { optionsGender } from 'utils/constants';
import { useSnackbar } from 'notistack';
import { useEditPatient } from 'hooks/patients/useEditPatient';
import { Company, DataPeriod, ErrorData } from 'interfaces/global/globalInterfaces';
import { requiredPassByCompany } from 'utils/requiredCheck';
import { ChangeProgramModal } from './ChangeProgramModal';

import { validationPatient } from './validationEditPatient';

export const EditPatientModal: React.FC<EditPatientPassModalProps> = ({
  patientData,
  selectedCompanies,
  periodByAttachOrDetach,
  open,
  handleClose,
  refetchPatient,
}) => {
  const [patientCompany, setPatientCompany] = useState<Company | null>(null);
  const [isChangeProgramModal, setIsChangeProgramModal] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const reqPass: boolean = requiredPassByCompany(patientCompany);

  const validPatient = validationPatient(reqPass);
  type PatientParams = z.infer<typeof validPatient>;


  const {
    setValue,
    reset,
    formState: { errors, isDirty, dirtyFields },
    setError,
    handleSubmit,
    control,
  } = useForm<PatientParams>({
    mode: 'onTouched',
    resolver: zodResolver(validationPatient(reqPass)),
  });

  const dirtyFullname = dirtyFields.first_name && dirtyFields.last_name && dirtyFields.middle_name;

  const mainFilterPeriodStaff: DataPeriod | null =
    'mainFilterPeriodStaff' in sessionStorage ? JSON.parse(sessionStorage.getItem('mainFilterPeriodStaff') || '') : '';

  const { programs: searchPrograms } = useSearchPrograms({
    company: patientCompany || null,
    period: mainFilterPeriodStaff,
    selectedProgram: null,
    selectedServices: [],
    selectedСity: null,
    fetchAllPages: true,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const programs = useMemo(
    () => mapProgramsToSelectOptions(searchPrograms, { withBudget: true }),
    [searchPrograms]
  );
  const patientProgram = useMemo(
    () => programs.find((program) => program.uuid === patientData.program_uuid) ?? null,
    [programs, patientData.program_uuid]
  );

  const { isLoadingEditPatient, handleEditPatient } = useEditPatient({
    patientUuid: patientData ? patientData.uuid : null,
    dirtyFields,

    onSuccess: async (res: Response) => {
      if (res.ok) {
        enqueueSnackbar('Успешно отредактировано!', { variant: 'success' });
        refetchPatient();

        setTimeout(() => {
          handleClose();
          reset();
        }, 5000);
      } else {
        await res.json().then(
          (data: {
            message: string;
            errors: {
              message: string;
              code: string;
              field:
                | 'last_name'
                | 'first_name'
                | 'middle_name'
                | 'gender'
                | 'birth_date'
                | 'nationality'
                | 'address'
                | 'phone'
                | 'greetings_email'
                | 'position'
                | 'number'
                | 'passport_number'
                | 'passport_date'
                | 'passport_given'
                | 'additional_info';
            }[];
          }) => {
            if (data.message !== 'Invalid input.') {
              enqueueSnackbar(data.message, { variant: 'error' });
            }

            data.errors.forEach((item) => {
              const { field } = item;

              enqueueSnackbar(item.message, { variant: 'error' });

              setError(field, {
                type: 'custom',
                message: item.message,
              });
            });
          }
        );
      }
    },
    onError: () => {
      enqueueSnackbar('Произошла ошибка при редактировании', { variant: 'error' });
    },
  });

  // Обертка для handleEditPatient, которая преобразует даты в формат yyyy-MM-dd перед отправкой
  const handleSubmitWithDateConversion = (data: PatientParams) => {
    const convertedData = { ...data };
    
    // Преобразуем birth_date в yyyy-MM-dd если есть значение
    if (convertedData.birth_date && convertedData.birth_date !== '') {
      try {
        // Пробуем распарсить как dd.MM.yyyy
        const parsedDate = parse(convertedData.birth_date, 'dd.MM.yyyy', new Date());
        if (isValid(parsedDate)) {
          convertedData.birth_date = format(parsedDate, 'yyyy-MM-dd');
        } else {
          // Если не dd.MM.yyyy, пробуем как yyyy-MM-dd (если уже в правильном формате, оставляем как есть)
          const parsedDate2 = parse(convertedData.birth_date, 'yyyy-MM-dd', new Date());
          if (!isValid(parsedDate2)) {
            // Если не удалось распарсить ни в одном формате, оставляем как есть
            // (на случай если формат другой, но валидный)
          }
        }
      } catch {
        // Если ошибка парсинга, оставляем как есть
      }
    }
    
    // Преобразуем passport_date в yyyy-MM-dd если есть значение
    if (convertedData.passport_date && convertedData.passport_date !== null && convertedData.passport_date !== '') {
      try {
        // Пробуем распарсить как dd.MM.yyyy
        const parsedDate = parse(convertedData.passport_date, 'dd.MM.yyyy', new Date());
        if (isValid(parsedDate)) {
          convertedData.passport_date = format(parsedDate, 'yyyy-MM-dd');
        } else {
          // Если не dd.MM.yyyy, пробуем как yyyy-MM-dd (если уже в правильном формате, оставляем как есть)
          const parsedDate2 = parse(convertedData.passport_date, 'yyyy-MM-dd', new Date());
          if (!isValid(parsedDate2)) {
            // Если не удалось распарсить ни в одном формате, оставляем как есть
            // (на случай если формат другой, но валидный)
          }
        }
      } catch {
        // Если ошибка парсинга, оставляем как есть
      }
    }
    
    handleEditPatient(convertedData);
  };

  useEffect(() => {
    reset({
      last_name: patientData?.last_name || '',
      first_name: patientData?.first_name || '',
      middle_name: patientData?.middle_name || '',
      gender: patientData?.gender === 'Мужской' ? 'M' : 'F',

      // Оставляем дату в формате dd.MM.yyyy для отображения в DatePicker
      birth_date: patientData?.birth_date || '',
      nationality: patientData?.nationality || '',
      address: patientData?.address || '',
      phone: patientData?.phone || '',
      greetings_email: patientData?.email || '',
      position: patientData?.position || '',
      number: patientData?.number || '',
      passport_number: patientData?.passport_number || '',
      // Оставляем дату в формате dd.MM.yyyy для отображения в DatePicker
      passport_date: patientData?.passport_date || null,
      passport_given: patientData?.passport_given || '',
      additional_info: patientData?.additional_info || '',
    });
  }, [patientData, reset]);

  useEffect(() => {
    if (selectedCompanies.length) {
      selectedCompanies.forEach((company) => {
        if (company.uuid === patientData.companyUuid) {
          setPatientCompany(company);
        }
      });
    }
  }, [patientData, selectedCompanies]);

  return (
    <Dialog open={open} onClose={handleClose} sx={dialogStyleAddModal}>
      <Box sx={modalHeaderStyle}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h3" sx={modalTitleStyle}>
            Редактирование пациента
          </Typography>
          <Typography variant="subtitle1" sx={modalSubtitleStyle}>
            Изменения будут применены в ближайшее время.
          </Typography>
        </Box>
        <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={handleClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box component="form" sx={modalFormStyle} onSubmit={handleSubmit(handleSubmitWithDateConversion)}>
        <Box sx={() => modalFormScrollStyle(600)}>
          <Box sx={() => modalFormWrapStyle(false)}>
            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="last_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Фамилия"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('last_name', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.last_name?.message}
                      helperProps={{
                        hint: errors.last_name?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('last_name', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="first_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Имя"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('first_name', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.first_name?.message}
                      helperProps={{
                        hint: errors.first_name?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('first_name', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="middle_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Отчество"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('middle_name', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.middle_name?.message}
                      helperProps={{
                        hint: errors.middle_name?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('middle_name', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field: { value, onChange } }) => {
                    // Преобразуем optionsGender в формат ComboboxOption (value должен быть строкой)
                    const genderOptions: ComboboxOption<string>[] = (optionsGender || []).map((gender) => ({
                      label: gender.title,
                      value: gender.value,
                    }));
                    
                    // Находим текущую выбранную опцию по значению
                    const selectedGenderOption = value
                      ? genderOptions.find((option) => option.value === value)
                      : undefined;
                    
                    return (
                      <>
                        <style>
                          {`
                            [data-radix-popper-content-wrapper] {
                              z-index: 1400 !important;
                            }
                          `}
                        </style>
                        <Combobox<string>
                          label="Пол"
                          placeholder="Пол"
                          options={genderOptions}
                          value={selectedGenderOption?.value}
                          searchable
                          clearable
                          size="m"
                          onChange={(newValue, option) => {
                            if (newValue && option) {
                              onChange(option.value);
                              setValue('gender', option.value, { shouldDirty: true });
                            } else {
                              onChange('');
                              setValue('gender', '', { shouldDirty: true });
                            }
                          }}
                        />
                      </>
                    );
                  }}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="birth_date"
                  render={({ field: { value, onChange } }) => {
                    const displayValue = value && value !== '' ? value : undefined;
                    
                    return (
                      <DatePicker
                        mode="single"
                        label="Дата рождения"
                        placeholder="Дата рождения"
                        value={displayValue}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue('birth_date', newValue, { shouldDirty: true });
                          } else {
                            onChange('');
                            setValue('birth_date', '', { shouldDirty: true });
                          }
                        }}
                        error={!!errors.birth_date?.message}
                        helperProps={{
                          hint: errors.birth_date?.message || '',
                          textColor: {
                            surface: 'light',
                            variant: 'secondary'
                          }
                        }}
                        required
                        size="m"
                      />
                    );
                  }}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="nationality"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Гражданство"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('nationality', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.nationality?.message}
                      helperProps={{
                        hint: errors.nationality?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('nationality', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="address"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Адрес фактического проживания"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('address', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.address?.message}
                      helperProps={{
                        hint: errors.address?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('address', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={() => modalFormWrapStyle(false)}>
            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { value } }) => (
                    <SpectrumTextField
                      label="Телефон"
                      value={value || ''}
                      disabled
                      error={!!errors.phone?.message}
                      helperProps={{
                        hint: errors.phone?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      size="m"
                    />
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="greetings_email"
                  render={({ field: { value } }) => (
                    <SpectrumTextField
                      label="E-mail"
                      value={value || ''}
                      disabled
                      error={!!errors.greetings_email?.message}
                      helperProps={{
                        hint: errors.greetings_email?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      size="m"
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="position"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Должность"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('position', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.position?.message}
                      helperProps={{
                        hint: errors.position?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('position', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="number"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Табельный номер"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('number', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.number?.message}
                      helperProps={{
                        hint: errors.number?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('number', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="passport_number"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Номер паспорта"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('passport_number', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.passport_number?.message}
                      helperProps={{
                        hint: errors.passport_number?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('passport_number', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="passport_date"
                  render={({ field: { value, onChange } }) => {
                    const displayValue = value && value !== null && value !== '' ? value : undefined;
                    
                    return (
                      <DatePicker
                        mode="single"
                        label="Дата выдачи"
                        placeholder="Дата выдачи"
                        value={displayValue}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue('passport_date', newValue, { shouldDirty: true });
                          } else {
                            onChange(null);
                            setValue('passport_date', null, { shouldDirty: true });
                          }
                        }}
                        error={!!errors.passport_date?.message}
                        helperProps={{
                          hint: errors.passport_date?.message || '',
                          textColor: {
                            surface: 'light',
                            variant: 'secondary'
                          }
                        }}
                        required={reqPass}
                        size="m"
                      />
                    );
                  }}
                />
              </Box>
            </Box>

            <Box sx={modalFormWrapInputsStyle}>
              <Box sx={() => modalFormInputStyle(false)}>
                <Controller
                  control={control}
                  name="passport_given"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SpectrumTextField
                      label="Кем выдан"
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value;
                        onChange(newValue);
                        setValue('passport_given', newValue, { shouldDirty: true });
                      }}
                      onBlur={onBlur}
                      error={!!errors.passport_given?.message}
                      helperProps={{
                        hint: errors.passport_given?.message || '',
                        textColor: {
                          surface: 'light',
                          variant: 'secondary'
                        }
                      }}
                      required
                      size="m"
                      rightElement={
                        value ? (
                          <IconButton
                            sx={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => {
                              setValue('passport_given', '', { shouldDirty: true });
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : undefined
                      }
                    />
                  )}
                />
              </Box>
            </Box>
            <Box sx={() => modalFormInputStyle(false)}>
              <Controller
                control={control}
                name="additional_info"
                render={({ field: { value, onChange, onBlur } }) => (
                  <SpectrumTextField
                    label="Доп информация"
                    value={value || ''}
                    onChange={(e) => {
                      const newValue = (e.target as HTMLInputElement).value;
                      onChange(newValue);
                      setValue('additional_info', newValue, { shouldDirty: true });
                    }}
                    onBlur={onBlur}
                    error={!!errors.additional_info?.message}
                    helperProps={{
                      hint: errors.additional_info?.message || '',
                      textColor: {
                        surface: 'light',
                        variant: 'secondary'
                      }
                    }}
                    size="m"
                    rightElement={
                      value ? (
                        <IconButton
                          sx={{ width: '28px', height: '28px', padding: 0 }}
                          onClick={() => {
                            setValue('additional_info', '', { shouldDirty: true });
                          }}
                        >
                          <CloseOutlinedIcon fontSize="small" />
                        </IconButton>
                      ) : undefined
                    }
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={() => modalFormWrapStyle(true)}>
          <Typography variant="caption" sx={{ color: grayLightDarkOpacity }}>
            <Typography variant="caption" sx={{ color: themeColor }}>
              *{' '}
            </Typography>
            - обязательные поля.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '32px',
            }}
          >
            <TooltipActionsInfo placement="left" title={dirtyFullname ? 'Нельзя редактировать ФИО полностью' : ''}>
              <Box component="span">
                <SpectrumButton
                  variant="promo"
                  size="s"
                  type="submit"
                  disabled={isLoadingEditPatient || !isDirty || dirtyFullname}
                >
                  Сохранить
                </SpectrumButton>
              </Box>
            </TooltipActionsInfo>
          </Box>
        </Box>
      </Box>

      {!!patientProgram && (
        <ChangeProgramModal
          open={isChangeProgramModal}
          handleClose={() => setIsChangeProgramModal(false)}
          periodByAttachOrDetach={periodByAttachOrDetach}
          parentClose={handleClose}
          patientData={patientData}
          programs={programs}
          patientProgram={patientProgram}
          refetchPatient={refetchPatient}
        />
      )}
    </Dialog>
  );
};
